import { useEffect } from 'react';

import Provision from '../../../../api/provision';
import Table from '../../../../components/Table';
import useApiQuery from '../../../../hooks/useApiQuery';

import type { ErrorType } from '@/types';
import type { TotalUsage, UsageByNodeType, VirtualResource } from '@/types/productTypes';
import type { TableField, TableRowData } from '@/types/tableTypes';


export interface UsageSummaryProps {
  juiceGroup: string;
  cloudId: string;
}

const fields: TableField[] = [
  { title: 'CN Type', id: 'CNType' },
  { title: 'Server #', id: 'server' },
  { title: 'Storage Volume (GB)', id: 'storageVolume' },
  { title: 'vCPU (Shared)', id: 'vCPUShared' },
  { title: 'vCPU (Dedicated)', id: 'vCPUDedicated' },
  { title: 'RAM (Shared)(GB)', id: 'RAMShared' },
  { title: 'RAM (Dedicated)(GB)', id: 'RAMDedicated' },
  { title: 'Root disk local (GB)', id: 'rootDiskLocal' },
  { title: 'Root disk (GB)', id: 'rootDisk' },
];

const getRows = (usageByNodeType: UsageByNodeType): TableRowData[] => {
  const CNTypes: (keyof UsageByNodeType)[] = ['shared_local', 'shared', 'dedicated', 'dedicated_local'];
  return CNTypes.map((CNType) => ({
    key: CNType,
    data: [
      { field: 'CNType', value: CNType },
      { field: 'server', value: usageByNodeType[CNType].vms_total },
      { field: 'storageVolume', value: '-' },
      { field: 'vCPUShared', value: usageByNodeType[CNType].cpus_shared_total },
      { field: 'vCPUDedicated', value: usageByNodeType[CNType].cpus_dedicated_total },
      { field: 'RAMShared', value: usageByNodeType[CNType].ram_shared_gigabytes },
      { field: 'RAMDedicated', value: usageByNodeType[CNType].ram_dedicated_gigabytes },
      { field: 'rootDiskLocal', value: usageByNodeType[CNType].root_disk_local_gigabytes },
      { field: 'rootDisk', value: usageByNodeType[CNType].root_disk_gigabytes },
    ],
  }));
};

const getTotals = (totalUsage: TotalUsage): TableRowData[] => [
  {
    key: 'total',
    data: [
      { field: 'CNType', value: 'total' },
      { field: 'server', value: totalUsage.vms_total },
      { field: 'storageVolume', value: totalUsage.volume_gigabytes },
      { field: 'vCPUShared', value: totalUsage.cpus_shared_total },
      { field: 'vCPUDedicated', value: totalUsage.cpus_dedicated_total },
      { field: 'RAMShared', value: totalUsage.ram_shared_gigabytes },
      { field: 'RAMDedicated', value: totalUsage.ram_dedicated_gigabytes },
      { field: 'rootDiskLocal', value: totalUsage.root_disk_local_gigabytes },
      { field: 'rootDisk', value: totalUsage.root_disk_gigabytes },
    ],
  },
];

const UsageSummary: React.FC<UsageSummaryProps> = ({ juiceGroup, cloudId }) => {
  const {
    data: virtualResources = [],
    error: fetchError,
    makeApiCall: fetchVirtualResources,
  } = useApiQuery<VirtualResource[]>(Provision.fetchOSVirtualResources);

  useEffect(() => {
    if (cloudId && juiceGroup) {
      fetchVirtualResources(juiceGroup, [cloudId]);
    }
  }, [cloudId, juiceGroup, fetchVirtualResources]);

  if (!virtualResources.length) return <p>No usage data found</p>;

  return (
    <Table
      fields={fields}
      rows={getRows(virtualResources[0]?.usage.by_compute_node_type ?? {
        shared_local: { vms_total: 0, cpus_shared_total: 0, cpus_dedicated_total: 0, ram_shared_gigabytes: 0, ram_dedicated_gigabytes: 0, root_disk_local_gigabytes: 0, root_disk_gigabytes: 0 },
        shared: { vms_total: 0, cpus_shared_total: 0, cpus_dedicated_total: 0, ram_shared_gigabytes: 0, ram_dedicated_gigabytes: 0, root_disk_local_gigabytes: 0, root_disk_gigabytes: 0 },
        dedicated: { vms_total: 0, cpus_shared_total: 0, cpus_dedicated_total: 0, ram_shared_gigabytes: 0, ram_dedicated_gigabytes: 0, root_disk_local_gigabytes: 0, root_disk_gigabytes: 0 },
        dedicated_local: { vms_total: 0, cpus_shared_total: 0, cpus_dedicated_total: 0, ram_shared_gigabytes: 0, ram_dedicated_gigabytes: 0, root_disk_local_gigabytes: 0, root_disk_gigabytes: 0 },
      })}
      footerRows={getTotals(virtualResources[0]?.usage.total ?? {
        vms_total: 0,
        cpus_shared_total: 0,
        cpus_dedicated_total: 0,
        ram_shared_gigabytes: 0,
        ram_dedicated_gigabytes: 0,
        root_disk_local_gigabytes: 0,
        root_disk_gigabytes: 0,
        volume_gigabytes: 0,
      })}
      search={false}
      pagination={false}
      error={fetchError as ErrorType}
    />
  );
};

export default UsageSummary;
